// src/components/workspace/RequirementCoveragePanel.tsx
// Requirement coverage matrix for the AI Tools panel: every binding solicitation
// requirement mapped to the proposal section that addresses it, sorted worst-first.
// "Address" on an uncovered/partial row drafts an approval-gated section edit.

import React, { useMemo, useState } from 'react';
import { ListChecks, RefreshCw, Loader2, CheckCircle, AlertTriangle, XCircle, Sparkles } from 'lucide-react';
import type { RequirementCoverage, RequirementCoverageItem } from '../../types/source-grounded';

interface RequirementCoveragePanelProps {
  coverage: RequirementCoverage | null;
  loading: boolean;
  onRun: () => void;
  onAddress: (item: RequirementCoverageItem) => void;
  onAcknowledge: (item: RequirementCoverageItem) => void;
  onAcknowledgeAll: () => void;
  addressDisabled?: boolean;
}

type Filter = 'all' | 'uncovered' | 'partial' | 'covered';

const STATUS_META: Record<RequirementCoverageItem['status'], { label: string; cls: string; icon: any; rank: number }> = {
  uncovered: { label: 'Uncovered', cls: 'text-urgent bg-urgent-bg border-urgent-border', icon: XCircle, rank: 0 },
  partial:   { label: 'Partial',   cls: 'text-fit-moderate bg-fit-moderate-bg border-fit-moderate-border', icon: AlertTriangle, rank: 1 },
  covered:   { label: 'Covered',   cls: 'text-success bg-success-bg border-success-border', icon: CheckCircle, rank: 2 },
};

export function RequirementCoveragePanel({ coverage, loading, onRun, onAddress, onAcknowledge, onAcknowledgeAll, addressDisabled }: RequirementCoveragePanelProps) {
  const [filter, setFilter] = useState<Filter>('all');

  // Content requirements (real win-blockers) vs acknowledgements (agree-to-comply).
  const contentItems = useMemo(
    () => (coverage?.items || []).filter((i) => i.kind !== 'acknowledgement'),
    [coverage],
  );
  const ackItems = useMemo(
    () => (coverage?.items || []).filter((i) => i.kind === 'acknowledgement'),
    [coverage],
  );

  const items = useMemo(() => {
    const list = [...contentItems];
    list.sort((a, b) => STATUS_META[a.status].rank - STATUS_META[b.status].rank);
    if (filter === 'all') return list;
    return list.filter((i) => i.status === filter);
  }, [contentItems, filter]);

  const ackOpen = ackItems.filter((i) => !i.acknowledged && i.status !== 'covered');
  const s = coverage?.summary;

  return (
    <div className="flex-1 overflow-y-auto p-3">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-bw-ink text-sm flex items-center gap-1.5">
          <ListChecks className="h-4 w-4 text-bw-primary" /> Requirement Coverage
        </h3>
        <button
          onClick={onRun}
          disabled={loading}
          className="flex items-center gap-1 text-xs text-bw-primary hover:brightness-110 disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          {coverage ? 'Re-check' : 'Run check'}
        </button>
      </div>

      {loading && !coverage && (
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-bw-primary" />
          <p className="text-xs text-bw-muted text-center">Mapping every solicitation requirement to your proposal…</p>
        </div>
      )}

      {!loading && !coverage && (
        <div className="text-center py-10">
          <ListChecks className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-bw-muted">Check that every "shall/must" in the solicitation is addressed.</p>
          <button
            onClick={onRun}
            className="mt-3 px-3 py-1.5 bg-bw-primary text-white text-xs font-medium rounded-md hover:brightness-110"
          >
            Run coverage check
          </button>
        </div>
      )}

      {coverage && (
        <>
          {/* Summary + filters */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            {(['uncovered', 'partial', 'covered'] as const).map((k) => {
              const meta = STATUS_META[k];
              const count = s ? (s as any)[k] : 0;
              const active = filter === k;
              return (
                <button
                  key={k}
                  onClick={() => setFilter(active ? 'all' : k)}
                  className={`rounded-lg p-2 border text-left transition-colors ${meta.cls} ${active ? 'ring-2 ring-bw-primary/40' : ''}`}
                >
                  <div className="text-lg font-bold leading-none">{count}</div>
                  <div className="text-[10px] font-medium mt-0.5">{meta.label}</div>
                </button>
              );
            })}
          </div>
          {coverage.note && <p className="text-xs text-bw-muted mb-2">{coverage.note}</p>}
          {filter !== 'all' && (
            <button onClick={() => setFilter('all')} className="text-[11px] text-bw-primary mb-2 hover:underline">
              Show all {s?.total ?? 0}
            </button>
          )}

          <div className="space-y-2">
            {items.map((item) => {
              const meta = STATUS_META[item.status];
              const Icon = meta.icon;
              const canFix = item.status !== 'covered';
              return (
                <div key={item.id} className="rounded-lg border border-bw-border p-2.5">
                  <div className="flex items-start gap-2">
                    <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${meta.cls.split(' ')[0]}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-bw-ink leading-snug">{item.requirement}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-bw-surface-subtle text-bw-muted">{item.source}</span>
                        {item.section && (
                          <span className="text-[10px] text-bw-muted">→ {item.section}</span>
                        )}
                        <span className="text-[10px] text-bw-muted">· {item.confidence} confidence</span>
                      </div>
                      {item.evidence && (
                        <p className="text-[11px] text-bw-muted mt-1 italic">{item.evidence}</p>
                      )}
                      {canFix && (
                        <button
                          onClick={() => onAddress(item)}
                          disabled={addressDisabled}
                          className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-bw-primary bg-bw-primary-tint hover:brightness-95 rounded disabled:opacity-50"
                          title="Draft a change to address this requirement (you approve before it's saved)"
                        >
                          <Sparkles className="h-3 w-3" /> Address
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {items.length === 0 && (
              <p className="text-xs text-bw-muted text-center py-6">No content requirements in this filter.</p>
            )}
          </div>

          {/* Acknowledgements — post-award / performance obligations you agree to
              comply with. NOT win-blockers; they just need acknowledgement. */}
          {ackItems.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-bw-ink flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5 text-bw-muted" />
                  Acknowledgements ({ackItems.length})
                </h4>
                {ackOpen.length > 0 && (
                  <button onClick={onAcknowledgeAll} className="text-[11px] text-bw-primary hover:underline">
                    Acknowledge all
                  </button>
                )}
              </div>
              <p className="text-[11px] text-bw-muted mb-2">
                Performance/post-award terms you agree to comply with — not content gaps that block the award.
              </p>
              <div className="space-y-2">
                {ackItems.map((item) => {
                  const done = item.acknowledged || item.status === 'covered';
                  return (
                    <div key={item.id} className="rounded-lg border border-bw-border p-2.5">
                      <div className="flex items-start gap-2">
                        <CheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${done ? 'text-success' : 'text-bw-muted'}`} />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-bw-ink leading-snug">{item.requirement}</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-bw-surface-subtle text-bw-muted">{item.source}</span>
                          </div>
                          {!done && (
                            <button
                              onClick={() => onAcknowledge(item)}
                              className="mt-1.5 inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-success bg-success-bg hover:brightness-95 rounded"
                            >
                              <CheckCircle className="h-3 w-3" /> Acknowledge &amp; comply
                            </button>
                          )}
                          {done && <span className="mt-1.5 inline-block text-[11px] text-success font-medium">✓ Acknowledged</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default RequirementCoveragePanel;
